/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package utils

import kotlinx.datetime.*
import kotlin.js.Date as JsDate

/**
 * Converts JavaScript Date to Kotlinx LocalDate
 */
fun JsDate.toLocalDate(): LocalDate {
    val year = this.getFullYear()
    val month = this.getMonth() + 1 // JavaScript months are 0-indexed
    val day = this.getDate()
    return LocalDate(year, month, day)
}

/**
 * Converts Kotlinx LocalDate to JavaScript Date
 */
fun LocalDate.toJsDate(): JsDate {
    val year = this.year
    val month = this.monthNumber - 1 // JavaScript months are 0-indexed
    val day = this.dayOfMonth
    return JsDate(year, month, day)
}

/**
 * Converts JavaScript Date to Kotlinx Instant
 */
fun JsDate.toInstant(): Instant {
    return Instant.fromEpochMilliseconds(this.getTime().toLong())
}

/**
 * Converts Kotlinx Instant to JavaScript Date
 */
fun Instant.toJsDate(): JsDate {
    return JsDate(this.toEpochMilliseconds().toDouble())
}

/**
 * Gets current date as LocalDate in JS
 */
fun getCurrentLocalDate(): LocalDate {
    return JsDate().toLocalDate()
}

/**
 * Gets current instant in JS
 */
fun getCurrentInstant(): Instant {
    return JsDate().toInstant()
}
